import React from 'react';

import { useHiveClient } from '@gitbook/hive-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import type { AgentChangeRequestRpc, AgentScopeOptions } from '@services/content-agents/browser';

import type { GitBookAgentClient, GitBookAgentClientOptions } from './GitBookAgentClient';
import { agentIsProcessing, useAgent, useAgentState, useGetAgent } from './useAgent';

type ChangeRequestAgentParams = {
    organizationId: string;
    spaceId: string;
    changeRequestId: string;

    /**
     * ID of the conversation to connect to. When omitted, connects to the default (legacy)
     * conversation, which is also the one used by background jobs.
     */
    conversationId?: string | null;

    /**
     * GID of the site the user is editing from, when in site-centric mode. Carried on the connection
     * so the agent can read and search the site's other spaces; omit it in space-centric mode.
     * Every connection to the same conversation must pass the same value so they pool onto one
     * socket.
     */
    siteId?: string;
};

type ChangeRequestAgentOptions = ChangeRequestAgentParams &
    Pick<GitBookAgentClientOptions, 'transcript'>;

/**
 * Build the websocket pathname for a change request agent conversation.
 *
 * The durable object is addressed by the path params, so connection-scoped intent rides on the
 * query string instead: `siteId` for the site the user is editing from (site-centric mode only).
 * Order-stable so connections to the same conversation pool onto one socket.
 */
export function getChangeRequestAgentPathname({
    organizationId,
    spaceId,
    changeRequestId,
    conversationId,
    siteId,
}: ChangeRequestAgentParams) {
    const base = `/agents/o/${organizationId}/s/${spaceId}/c/${changeRequestId}`;
    const path = conversationId ? `${base}/conversation/${conversationId}` : base;
    const query = new URLSearchParams();
    if (siteId) {
        query.set('siteId', siteId);
    }
    const queryString = query.toString();
    return queryString ? `${path}?${queryString}` : path;
}

/**
 * Return a function to imperatively access a change request agent.
 */
export function useGetChangeRequestAgent() {
    const getAgent = useGetAgent();
    const hiveClient = useHiveClient();
    return React.useCallback(
        (params: ChangeRequestAgentParams) => {
            return getAgent<AgentChangeRequestRpc>({
                pathname: getChangeRequestAgentPathname(params),
                getToken: async () => hiveClient.auth.getToken(params.spaceId),
            });
        },
        [hiveClient, getAgent]
    );
}

/**
 * Hook to connect to the agent for a change request.
 *
 * Pass `enabled: false` to skip connecting (returns `null`), for when the agent is only needed
 * conditionally, and `transcript` to choose what the connection receives
 * ({@link GitBookAgentClientOptions.transcript}).
 */
export function useChangeRequestAgent(
    input: ChangeRequestAgentOptions & { enabled: boolean }
): GitBookAgentClient<AgentChangeRequestRpc> | null;
export function useChangeRequestAgent(
    input: ChangeRequestAgentOptions
): GitBookAgentClient<AgentChangeRequestRpc>;
export function useChangeRequestAgent(
    input: ChangeRequestAgentOptions & { enabled?: boolean }
): GitBookAgentClient<AgentChangeRequestRpc> | null {
    const { spaceId } = input;
    const hiveClient = useHiveClient();
    const agent = useAgent<AgentChangeRequestRpc>({
        pathname: getChangeRequestAgentPathname(input),
        getToken: async () => hiveClient.auth.getToken(spaceId),
        enabled: input.enabled ?? true,
        transcript: input.transcript,
    });

    return agent;
}

/**
 * Run and track a background agent task in a change request, collaboratively.
 *
 * The task runs on its own conversation — its own durable object, keyed by `taskId` — so several
 * tasks run in parallel without interfering (a durable object runs one job at a time). Every client
 * pointed at the same `taskId` shares that durable object's broadcast state, so `isRunning` is true
 * for everyone while the task runs, whoever started it. `run` is idempotent: starting a task that
 * is already running is a no-op, so concurrent clients converge on the one running task.
 *
 * Pass `enabled: false` to skip connecting when there is nothing to track (no socket is opened); in
 * that case `isRunning` is always false and `run` is a no-op.
 */
export function useChangeRequestAgentTask(params: {
    organizationId: string;
    spaceId: string;
    changeRequestId: string;
    /** Identifies the task and the conversation (durable object) that isolates its execution. */
    taskId: string;
    /** Connect and track only when relevant. Defaults to true. */
    enabled?: boolean;
}): {
    isRunning: boolean;
    run: (prompt: string, scopeOptions?: AgentScopeOptions) => Promise<void>;
} {
    const { organizationId, spaceId, changeRequestId, taskId, enabled = true } = params;

    const agent = useChangeRequestAgent({
        organizationId,
        spaceId,
        changeRequestId,
        conversationId: taskId,
        enabled,
    });

    const isRunning = useAgentState(agent, agentIsProcessing);

    const run = useEventCallback(async (prompt: string, scopeOptions?: AgentScopeOptions) => {
        // When enabled the agent is connected; guard anyway so a disabled task is a safe no-op.
        await agent?.runTask(prompt, scopeOptions);
    });

    return { isRunning, run };
}
